export const portfolioContent = {
  en: {
    meta: {
      languageLabel: 'PT',
      resumeLabel: 'Resume',
      resumeHref: '/Icaro-Felipe-de-Lanes-Resume.html',
      role: 'Software Developer',
      footer:
        '© 2026 Icaro Felipe de Lanes. Built to present real software work with business context.',
    },
    navigation: {
      brandRole: 'Software Developer',
      items: [
        { name: 'About', href: '#about' },
        { name: 'What I Build', href: '#services' },
        { name: 'Projects', href: '#projects' },
        { name: 'Experience', href: '#experience' },
        { name: 'AI & Workflow', href: '#ai-workflow' },
        { name: 'Consulting', href: '#consulting' },
        { name: 'Stack', href: '#stack' },
        { name: 'Contact', href: '#contact' },
      ],
    },
    hero: {
      eyebrow: 'Software Developer | Brazil',
      title: 'I build internal systems and automation for real operations.',
      lead:
        'Software Developer focused on internal systems, process automation, integrations, and business applications for operations-driven teams.',
      support:
        'My background in logistics and operations helps me understand where processes break, where teams lose time, and where software can create measurable value.',
      ctas: {
        projects: 'View Projects',
        linkedin: 'LinkedIn',
        github: 'GitHub',
        resume: 'Resume',
        contact: 'Contact',
      },
      profile: {
        name: 'Icaro Felipe de Lanes',
        role: 'Software Developer',
      },
      signals: [
        {
          label: 'Focus',
          text: 'Internal platforms, process automation, business systems.',
        },
        {
          label: 'Context',
          text: 'Logistics, operations, internal tooling, ERP-connected workflows.',
        },
        {
          label: 'Approach',
          text: 'Understand the process first, then build the right software around it.',
        },
      ],
    },
    about: {
      eyebrow: 'About',
      title: 'Professional background with technical and operational depth.',
      intro:
        'I am a Software Developer from Brazil focused on systems that improve internal execution, reduce manual work, and support business operations with more clarity and control.',
      cards: [
        {
          title: 'What I build',
          paragraphs: [
            'I work on internal platforms, process automation tools, dashboards, integrations, and mobile applications that support real business and operational workflows.',
            'My strongest fit is in environments where software needs to solve process bottlenecks, connect systems, and help teams execute better.',
          ],
        },
        {
          title: 'Why operations matter',
          paragraphs: [
            'Before working fully as a developer, I spent years in operational and logistics environments. That changed how I think about software.',
            'I do not see systems only as features. I see them as tools that must fit real routines, real constraints, and real business decisions.',
          ],
        },
      ],
    },
    services: {
      eyebrow: 'What I Build',
      title:
        'Software aligned with business execution, not just presentation layers.',
      intro:
        'The goal is usually the same: make work easier to execute, easier to control, and less dependent on manual coordination.',
      items: [
        {
          title: 'Internal Web Platforms',
          description:
            'Business-focused web applications for internal workflows, approvals, records, and day-to-day operations.',
          value:
            'Built to replace fragmented manual routines with structured, maintainable systems teams can actually use.',
        },
        {
          title: 'Process Automation Tools',
          description:
            'Tools that reduce repetitive work, standardize execution, and connect business rules to software logic.',
          value:
            'Useful when operations depend too much on spreadsheets, messaging, or manual follow-up.',
        },
        {
          title: 'Business Systems',
          description:
            'Operational systems for logistics, internal controls, service flows, and business-specific processes.',
          value:
            'Designed around how teams really work, not just around screens and forms.',
        },
        {
          title: 'APIs and Integrations',
          description:
            'Integrations between internal tools, ERPs, databases, and third-party platforms.',
          value:
            'Important for reducing duplicate work and making information move reliably across the business.',
        },
        {
          title: 'Mobile Apps for Internal Use',
          description:
            'Mobile applications that support field operations, internal requests, and on-the-go execution.',
          value:
            'Useful when the work does not happen at a desk and the process needs speed and traceability.',
        },
        {
          title: 'Dashboards and Operational Tools',
          description:
            'Dashboards, reporting portals, and internal monitoring tools for decision-making and execution.',
          value:
            'Created to improve visibility, reduce blind spots, and help teams act faster with better information.',
        },
      ],
    },
    projects: {
      eyebrow: 'Projects',
      title: 'Selected real projects presented with clear software context.',
      intro:
        'These projects reflect the kind of work I enjoy most: internal tools, integrated systems, operational applications, and software with clear business use.',
      caseStudyLabel: 'Project',
      problemLabel: 'Problem',
      solutionLabel: 'Solution',
      impactLabel: 'Impact',
      roleLabel: 'My Role',
      stackLabel: 'Stack',
      openProjectLabel: 'Open Project',
      items: [
        {
          title: 'ZPL Web Editor with Drag-and-Drop',
          category: 'Solution / Real Project',
          badge: 'Project in production',
          link: 'https://zpl.icarolanes.com.br',
          problem:
            'Creating ZPL labels directly in code made the process slower, more technical, and harder to adjust visually during operational use.',
          solution:
            'Built a visual web tool for creating ZPL labels with drag-and-drop editing, reducing the need to write label code manually.',
          stack: ['Next.js', 'React', 'Konva.js', 'ZPL'],
          impact:
            'Made label creation faster and more accessible, allowing layout adjustments through a visual interface instead of manual ZPL editing.',
          role:
            'Product design, frontend development, visual editor implementation, and ZPL generation workflow.',
        },
        {
          title: 'Operational Management System',
          category: 'Solution / Real Project',
          badge: 'Project in production',
          problem:
            'Logistics teams needed a structured internal system to manage operational routines, connect scheduling flows, and reduce process friction.',
          solution:
            'Developed an internal business application integrated with TOTVS LOGIX, focused on operational control, workflow consistency, and daily execution.',
          stack: ['C#', 'DevExpress XAF/XPO', 'TOTVS LOGIX', 'SQL Server'],
          impact:
            'Improved process organization and remains in active use at Comlog, continuing to evolve in a lean two-developer environment.',
          role:
            'Analysis, software development, business rule implementation, and ERP integration.',
        },
        {
          title: 'Internal Reporting Portal',
          category: 'Solution / Real Project',
          badge: 'Project in production',
          problem:
            'Operational information was harder to access and teams depended too much on manual extraction and spreadsheet-based reporting in the port terminal environment.',
          solution:
            'Built an internal PHP portal with Bootstrap for reports, dashboards, and operational data access, centralizing information for business users in one place. I developed it between 2021 and 2022 while still working in operations and studying Information Systems.',
          stack: ['PHP', 'Bootstrap', 'TOTVS SARA', 'SQL'],
          impact:
            'Reduced spreadsheet dependence and is still in use at the port for recurring operational reporting.',
          role:
            'Solo development, interface implementation, data handling, and operational reporting logic.',
        },
        {
          title: 'Mobile Version of the Operational Management System',
          category: 'Solution / Real Project',
          badge: 'Project in production',
          problem:
            'The operational management system also needed a mobile version so field and operational workflows could use the same business context away from the desktop.',
          solution:
            'Built the mobile version of the internal operational system used at Comlog, extending the same operational logic and ERP-connected workflow to mobile use with .NET MAUI.',
          stack: ['C#', '.NET MAUI', 'TOTVS LOGIX', 'API Integration'],
          impact:
            'Supported operational execution in mobile contexts and remains in use at Comlog as the product continues to grow.',
          role:
            'Mobile application development, ERP integration, and adaptation of the internal system experience for operational mobile use.',
        },
      ],
    },
    experience: {
      eyebrow: 'Experience Snapshot',
      title: 'A trajectory built from operations into software delivery.',
      intro:
        'That path is part of the value I bring: I can connect technical work to the reality of internal teams, operational routines, and process needs.',
      items: [
        {
          label: 'Current role',
          text: 'Software Developer building internal systems, integrations, and operational solutions in a logistics environment.',
        },
        {
          label: 'Previous evolution',
          text: 'Background across support, infrastructure, and business operations before moving fully into software development.',
        },
        {
          label: 'Operational depth',
          text: 'Hands-on experience in logistics and internal processes helps translate bottlenecks into software requirements.',
        },
        {
          label: 'Working style',
          text: 'Focused on useful software, pragmatic delivery, maintainable solutions, and business context.',
        },
      ],
    },
    aiWorkflow: {
      eyebrow: 'AI & Workflow',
      title: 'Using AI where it improves execution, automation, and system workflows.',
      intro:
        'I am interested in AI as applied software, not as hype. The value for me is using AI in operational routines, conversational flows, CRM contexts, integrations, and automation scenarios that make teams faster and processes more reliable.',
      cards: [
        {
          title: 'How I use AI in practice',
          paragraphs: [
            'I work with AI-oriented flows in CRM and conversational scenarios, including automation logic, agent behavior, business-context responses, and system-connected workflows.',
            'This includes thinking through how AI fits the process, where it should act, when it should escalate, and how it connects to operational rules and system actions.',
          ],
        },
        {
          title: 'What this adds to my profile',
          paragraphs: [
            'It strengthens my ability to work on modern business software where automation, integrations, and AI-assisted execution are becoming part of real delivery.',
            'For recruiters and teams, this means I can contribute not only with application development, but also with practical implementation of AI-enabled workflows in business environments.',
          ],
        },
      ],
      toolsLabel: 'Tools and areas I work with',
      tools: [
        'OpenAI',
        'Gemini',
        'AI-assisted workflows',
        'Prompt structuring',
        'Conversational CRM logic',
        'Automation flows',
        'Webhook-based integrations',
        'Business process mapping',
      ],
    },
    consulting: {
      eyebrow: 'Consulting',
      title: 'CRM implementation and integration consulting for real business operations.',
      intro:
        'Alongside software development, I also work on conversational CRM implementation and integration. My role is to help the client structure the environment, adapt the platform to the business workflow, and connect the solution to the operational reality of the team.',
      linkLabel: 'View the CRM solution page',
      linkHref: 'https://www.duoconecta.com.br/duozapia',
      cards: [
        {
          title: 'My role in this work',
          paragraphs: [
            'I work as the implementation and integration lead in CRM projects, helping structure onboarding, process adaptation, setup, and operational use.',
            'In practice, I participate in discovery, implementation planning, workflow mapping, platform configuration, and integration alignment with client systems.',
          ],
        },
        {
          title: 'What I deliver in these projects',
          paragraphs: [
            'This work involves implementation ownership, process fit, CRM operation setup, AI-enabled flow configuration, and integration planning based on the client context.',
            'The value I bring is not just making the platform available, but making it usable, connected, and aligned with how the client actually operates.',
          ],
        },
      ],
      bullets: [
        'Implementation planning and onboarding',
        'CRM setup aligned with business workflow',
        'Integration mapping with client systems and processes',
        'Operational use-case definition for automation and AI-assisted service',
        'Workflow adaptation for sales, service, and internal execution',
      ],
      note:
        'This work complements my software background by combining implementation ownership, business-process understanding, and system integration in customer-facing operations.',
    },
    stack: {
      eyebrow: 'Tech Stack',
      title: 'Organized around the kind of systems I build.',
      intro:
        'The stack matters, but the value comes from using it in the right context: internal products, business software, integrations, and process-focused applications.',
      groups: [
        {
          title: 'Frontend',
          items: ['React.js', 'Next.js', 'HTML', 'CSS', 'JavaScript'],
        },
        {
          title: 'Backend',
          items: ['Node.js', 'C#', 'PHP', 'REST APIs', 'ERP Integrations'],
        },
        {
          title: 'AI / Automation',
          items: ['OpenAI', 'Gemini', 'AI Agents', 'Automation Flows', 'Webhook Integrations'],
        },
        {
          title: 'Mobile',
          items: ['React Native', '.NET MAUI'],
        },
        {
          title: 'Databases',
          items: ['SQL Server', 'MySQL', 'Firebase'],
        },
        {
          title: 'Tools / Platforms',
          items: ['DevExpress', 'TOTVS', 'Git', 'Operational Reporting'],
        },
      ],
    },
    contact: {
      eyebrow: 'Contact',
      title:
        'Open to conversations about internal systems, product delivery, and remote opportunities.',
      intro:
        'If you are hiring for software development, building internal tools, or need someone who can connect business processes to practical software solutions, I would be glad to talk.',
      primaryCta: 'Contact Me',
      secondaryCta: 'View LinkedIn',
      links: [
        {
          label: 'LinkedIn',
          value: 'linkedin.com/in/icarolanes',
          href: 'https://linkedin.com/in/icarolanes',
        },
        {
          label: 'GitHub',
          value: 'github.com/icarolanes',
          href: 'https://github.com/icarolanes',
        },
        {
          label: 'Email',
          value: 'icaro.lanes@gmail.com',
          href: 'mailto:icaro.lanes@gmail.com',
        },
      ],
    },
    legal: {
      cookiesLabel: 'Cookie Policy',
      cookiesHref: '/cookie-policy.html',
      privacyLabel: 'Privacy Notice',
      privacyHref: '/privacy-notice.html',
      manageLabel: 'Cookie settings',
    },
  },
  pt: {
    meta: {
      languageLabel: 'EN',
      resumeLabel: 'Currículo',
      resumeHref: '/Icaro-Felipe-de-Lanes-Curriculo.html',
      role: 'Desenvolvedor de Software',
      footer:
        '© 2026 Icaro Felipe de Lanes. Feito para apresentar trabalho real em software com contexto de negócio.',
    },
    navigation: {
      brandRole: 'Desenvolvedor de Software',
      items: [
        { name: 'Sobre', href: '#about' },
        { name: 'O que eu construo', href: '#services' },
        { name: 'Projetos', href: '#projects' },
        { name: 'Experiência', href: '#experience' },
        { name: 'IA & Fluxos', href: '#ai-workflow' },
        { name: 'Consultoria', href: '#consulting' },
        { name: 'Stack', href: '#stack' },
        { name: 'Contato', href: '#contact' },
      ],
    },
    hero: {
      eyebrow: 'Desenvolvedor de Software | Brasil',
      title: 'Eu construo sistemas internos e automações para operações reais.',
      lead:
        'Desenvolvedor de Software focado em sistemas internos, automação de processos, integrações e aplicações de negócio para times orientados à operação.',
      support:
        'Minha base em logística e operações me ajuda a entender onde os processos quebram, onde os times perdem tempo e onde o software pode gerar valor de forma concreta.',
      ctas: {
        projects: 'Ver Projetos',
        linkedin: 'LinkedIn',
        github: 'GitHub',
        resume: 'Currículo',
        contact: 'Contato',
      },
      profile: {
        name: 'Icaro Felipe de Lanes',
        role: 'Desenvolvedor de Software',
      },
      signals: [
        {
          label: 'Foco',
          text: 'Plataformas internas, automação de processos e sistemas de negócio.',
        },
        {
          label: 'Contexto',
          text: 'Logística, operações, ferramentas internas e fluxos conectados a ERP.',
        },
        {
          label: 'Abordagem',
          text: 'Entender primeiro o processo para depois construir o software certo ao redor dele.',
        },
      ],
    },
    about: {
      eyebrow: 'Sobre',
      title: 'Trajetória profissional com profundidade técnica e operacional.',
      intro:
        'Sou um Desenvolvedor de Software do Brasil focado em sistemas que melhoram a execução interna, reduzem trabalho manual e dão mais clareza e controle para a operação.',
      cards: [
        {
          title: 'O que eu construo',
          paragraphs: [
            'Atuo em plataformas internas, ferramentas de automação de processos, dashboards, integrações e aplicações mobile que apoiam fluxos reais de negócio e operação.',
            'Meu melhor encaixe está em ambientes onde o software precisa resolver gargalos de processo, conectar sistemas e ajudar os times a executar melhor.',
          ],
        },
        {
          title: 'Por que operações importam',
          paragraphs: [
            'Antes de atuar integralmente como desenvolvedor, passei anos em ambientes operacionais e logísticos. Isso mudou a forma como penso software.',
            'Eu não vejo sistemas apenas como funcionalidades. Vejo como ferramentas que precisam servir rotinas reais, restrições reais e decisões reais de negócio.',
          ],
        },
      ],
    },
    services: {
      eyebrow: 'O que eu construo',
      title:
        'Software alinhado à execução do negócio, não apenas à camada de apresentação.',
      intro:
        'O objetivo costuma ser o mesmo: tornar o trabalho mais simples de executar, mais fácil de controlar e menos dependente de coordenação manual.',
      items: [
        {
          title: 'Plataformas Web Internas',
          description:
            'Aplicações web voltadas para fluxos internos, aprovações, registros e rotinas operacionais do dia a dia.',
          value:
            'Feitas para substituir rotinas manuais fragmentadas por sistemas estruturados e sustentáveis para o time.',
        },
        {
          title: 'Ferramentas de Automação de Processos',
          description:
            'Ferramentas que reduzem trabalho repetitivo, padronizam a execução e conectam regras de negócio à lógica do sistema.',
          value:
            'Úteis quando a operação depende demais de planilhas, mensagens ou acompanhamento manual.',
        },
        {
          title: 'Sistemas de Negócio',
          description:
            'Sistemas operacionais para logística, controles internos, fluxos de atendimento e processos específicos do negócio.',
          value:
            'Pensados em volta de como os times realmente trabalham, e não apenas em telas e formulários.',
        },
        {
          title: 'APIs e Integrações',
          description:
            'Integrações entre ferramentas internas, ERPs, bancos de dados e plataformas de terceiros.',
          value:
            'Importantes para reduzir retrabalho e fazer a informação circular com mais confiabilidade.',
        },
        {
          title: 'Apps Mobile para Uso Interno',
          description:
            'Aplicações mobile que apoiam operações de campo, solicitações internas e execução fora da mesa.',
          value:
            'Úteis quando o trabalho não acontece em um desktop e o processo precisa de velocidade e rastreabilidade.',
        },
        {
          title: 'Dashboards e Ferramentas Operacionais',
          description:
            'Dashboards, portais de relatório e ferramentas internas de acompanhamento para apoiar decisão e execução.',
          value:
            'Criados para aumentar visibilidade, reduzir pontos cegos e ajudar times a agir mais rápido com melhor informação.',
        },
      ],
    },
    projects: {
      eyebrow: 'Projetos',
      title: 'Projetos reais selecionados apresentados com contexto claro de software.',
      intro:
        'Esses projetos refletem o tipo de trabalho com que mais gosto de atuar: ferramentas internas, sistemas integrados, aplicações operacionais e software com uso de negócio bem definido.',
      caseStudyLabel: 'Projeto',
      problemLabel: 'Problema',
      solutionLabel: 'Solução',
      impactLabel: 'Impacto',
      roleLabel: 'Meu Papel',
      stackLabel: 'Stack',
      openProjectLabel: 'Abrir Projeto',
      items: [
        {
          title: 'Editor ZPL Web com Drag-and-Drop',
          category: 'Solução / Projeto Real',
          badge: 'Projeto em produção',
          link: 'https://zpl.icarolanes.com.br',
          problem:
            'A criação de etiquetas ZPL diretamente em código tornava o processo mais lento, técnico e difícil de ajustar visualmente durante o uso operacional.',
          solution:
            'Criação de uma ferramenta web visual para montar etiquetas ZPL com edição drag-and-drop, reduzindo a necessidade de escrever o código da etiqueta manualmente.',
          stack: ['Next.js', 'React', 'Konva.js', 'ZPL'],
          impact:
            'Tornou a criação de etiquetas mais rápida e acessível, permitindo ajustes de layout por uma interface visual em vez de edição manual de ZPL.',
          role:
            'Desenho da solução, desenvolvimento frontend, implementação do editor visual e fluxo de geração de ZPL.',
        },
        {
          title: 'Sistema de Gestão Operacional',
          category: 'Solução / Projeto Real',
          badge: 'Projeto em produção',
          problem:
            'Os times de logística precisavam de um sistema interno estruturado para gerenciar rotinas operacionais, conectar fluxos de agendamento e reduzir atrito nos processos.',
          solution:
            'Desenvolvimento de uma aplicação de negócio integrada ao TOTVS LOGIX, com foco em controle operacional, consistência de fluxo e execução do dia a dia.',
          stack: ['C#', 'DevExpress XAF/XPO', 'TOTVS LOGIX', 'SQL Server'],
          impact:
            'Melhorou a organização dos processos e segue em uso na Comlog, evoluindo continuamente em um ambiente enxuto com dois desenvolvedores.',
          role:
            'Análise, desenvolvimento de software, implementação de regras de negócio e integração com ERP.',
        },
        {
          title: 'Portal Interno de Relatórios',
          category: 'Solução / Projeto Real',
          badge: 'Projeto em produção',
          problem:
            'As informações operacionais eram mais difíceis de acessar e os times dependiam demais de extrações manuais e relatórios em planilhas no contexto do terminal portuário.',
          solution:
            'Criação de um portal interno em PHP com Bootstrap para relatórios, dashboards e acesso a dados operacionais, centralizando informação estruturada em um único lugar. Desenvolvi esse projeto entre 2021 e 2022, quando ainda atuava na operação e cursava Sistemas de Informação.',
          stack: ['PHP', 'Bootstrap', 'TOTVS SARA', 'SQL'],
          impact:
            'Reduziu a dependência de planilhas e segue em uso no porto para relatórios operacionais recorrentes.',
          role:
            'Desenvolvimento solo, implementação de interface, tratamento de dados e lógica de relatórios operacionais.',
        },
        {
          title: 'Versão Mobile do Sistema de Gestão Operacional',
          category: 'Solução / Projeto Real',
          badge: 'Projeto em produção',
          problem:
            'O sistema de gestão operacional também precisava de uma versão mobile para que fluxos de campo e de operação usassem o mesmo contexto de negócio fora do desktop.',
          solution:
            'Criação da versão mobile do sistema operacional interno usado na Comlog, estendendo a mesma lógica operacional e o mesmo fluxo conectado ao ERP para uso em dispositivos móveis.',
          stack: ['C#', '.NET MAUI', 'TOTVS LOGIX', 'Integração de APIs'],
          impact:
            'Apoiou a execução operacional em contextos mobile e segue em uso na Comlog conforme o produto continua crescendo.',
          role:
            'Desenvolvimento da aplicação mobile, integração com ERP e adaptação da experiência do sistema interno para uso operacional em campo.',
        },
      ],
    },
    experience: {
      eyebrow: 'Resumo de Experiência',
      title: 'Uma trajetória construída da operação até a entrega de software.',
      intro:
        'Esse caminho faz parte do valor que entrego: consigo conectar trabalho técnico com a realidade de times internos, rotinas operacionais e necessidades de processo.',
      items: [
        {
          label: 'Atuação atual',
          text: 'Desenvolvedor de Software construindo sistemas internos, integrações e soluções operacionais em contexto logístico.',
        },
        {
          label: 'Evolução anterior',
          text: 'Base em suporte, infraestrutura e operações antes da migração completa para desenvolvimento.',
        },
        {
          label: 'Profundidade operacional',
          text: 'Experiência prática em logística e processos internos que ajuda a traduzir gargalos em requisitos de software.',
        },
        {
          label: 'Forma de trabalhar',
          text: 'Foco em software útil, entrega pragmática, soluções sustentáveis e contexto de negócio.',
        },
      ],
    },
    aiWorkflow: {
      eyebrow: 'IA & Fluxos',
      title: 'Uso IA onde ela melhora execução, automação e fluxos de sistema.',
      intro:
        'Meu interesse em IA é aplicado, não apenas conceitual. O valor está em usar IA em rotinas operacionais, fluxos conversacionais, contextos de CRM, integrações e automações que deixam o time mais rápido e o processo mais confiável.',
      cards: [
        {
          title: 'Como uso IA na prática',
          paragraphs: [
            'Atuo com fluxos orientados por IA em cenários de CRM e conversação, incluindo lógica de automação, comportamento de agentes, respostas com contexto de negócio e fluxos conectados a sistemas.',
            'Isso envolve pensar onde a IA faz sentido no processo, onde ela deve agir, quando deve escalar para humano e como se conecta a regras operacionais e ações do sistema.',
          ],
        },
        {
          title: 'O que isso adiciona ao meu perfil',
          paragraphs: [
            'Isso fortalece minha atuação em software de negócio moderno, onde automação, integrações e execução assistida por IA já fazem parte da entrega real.',
            'Para recrutadores e times, isso significa que posso contribuir não só com desenvolvimento de aplicação, mas também com implementação prática de fluxos habilitados por IA em ambientes empresariais.',
          ],
        },
      ],
      toolsLabel: 'Ferramentas e frentes em que atuo',
      tools: [
        'OpenAI',
        'Gemini',
        'Fluxos assistidos por IA',
        'Estruturação de prompts',
        'Lógica de CRM conversacional',
        'Fluxos de automação',
        'Integrações via webhook',
        'Mapeamento de processo de negócio',
      ],
    },
    consulting: {
      eyebrow: 'Consultoria',
      title: 'Consultoria de implantação e integração de CRM para operações reais.',
      intro:
        'Além da atuação em desenvolvimento de software, também trabalho com implantação e integração de CRM conversacional. Meu papel é ajudar o cliente a estruturar o ambiente, adaptar a plataforma ao fluxo do negócio e conectar a solução à realidade operacional do time.',
      linkLabel: 'Ver página da solução de CRM',
      linkHref: 'https://www.duoconecta.com.br/duozapia',
      cards: [
        {
          title: 'Meu papel nesse trabalho',
          paragraphs: [
            'Atuo como responsável pela implantação e integração em projetos de CRM, ajudando a estruturar onboarding, adaptação de processo, configuração e uso operacional.',
            'Na prática, participo de discovery, planejamento de implantação, mapeamento de fluxo, configuração da plataforma e alinhamento de integrações com os sistemas do cliente.',
          ],
        },
        {
          title: 'O que eu entrego nesses projetos',
          paragraphs: [
            'Esse trabalho envolve assumir a implantação, ajustar aderência ao processo, estruturar a operação no CRM, configurar fluxos com IA e planejar integrações conforme o contexto do cliente.',
            'O valor que entrego não está apenas em disponibilizar a plataforma, mas em fazer com que ela seja utilizável, conectada e alinhada à forma como o cliente realmente opera.',
          ],
        },
      ],
      bullets: [
        'Planejamento de implantação e onboarding',
        'Configuração do CRM alinhada ao fluxo do cliente',
        'Mapeamento de integrações com sistemas e processos do cliente',
        'Definição de casos de uso para automação e atendimento com IA',
        'Adaptação de fluxo para vendas, atendimento e execução interna',
      ],
      note:
        'Essa atuação complementa minha base em software ao combinar responsabilidade de implantação, leitura de processo e integração de sistemas em operações voltadas ao cliente.',
    },
    stack: {
      eyebrow: 'Stack',
      title: 'Organizada em torno do tipo de sistema que eu construo.',
      intro:
        'A stack importa, mas o valor está em usá-la no contexto certo: produtos internos, software de negócio, integrações e aplicações orientadas a processo.',
      groups: [
        {
          title: 'Frontend',
          items: ['React.js', 'Next.js', 'HTML', 'CSS', 'JavaScript'],
        },
        {
          title: 'Backend',
          items: ['Node.js', 'C#', 'PHP', 'APIs REST', 'Integrações com ERP'],
        },
        {
          title: 'IA / Automação',
          items: ['OpenAI', 'Gemini', 'Agentes de IA', 'Fluxos de Automação', 'Integrações via Webhook'],
        },
        {
          title: 'Mobile',
          items: ['React Native', '.NET MAUI'],
        },
        {
          title: 'Bancos de Dados',
          items: ['SQL Server', 'MySQL', 'Firebase'],
        },
        {
          title: 'Ferramentas / Plataformas',
          items: ['DevExpress', 'TOTVS', 'Git', 'Relatórios Operacionais'],
        },
      ],
    },
    contact: {
      eyebrow: 'Contato',
      title:
        'Aberto a conversas sobre sistemas internos, entrega de produto e oportunidades remotas.',
      intro:
        'Se você está contratando para desenvolvimento de software, construindo ferramentas internas ou precisa de alguém que conecte processos de negócio a soluções práticas de software, será um prazer conversar.',
      primaryCta: 'Entrar em Contato',
      secondaryCta: 'Ver LinkedIn',
      links: [
        {
          label: 'LinkedIn',
          value: 'linkedin.com/in/icarolanes',
          href: 'https://linkedin.com/in/icarolanes',
        },
        {
          label: 'GitHub',
          value: 'github.com/icarolanes',
          href: 'https://github.com/icarolanes',
        },
        {
          label: 'Email',
          value: 'icaro.lanes@gmail.com',
          href: 'mailto:icaro.lanes@gmail.com',
        },
      ],
    },
    legal: {
      cookiesLabel: 'Politica de Cookies',
      cookiesHref: '/politica-de-cookies.html',
      privacyLabel: 'Aviso de Privacidade',
      privacyHref: '/aviso-de-privacidade.html',
      manageLabel: 'Configurar cookies',
    },
  },
};

